import { useState } from 'react';
import { CheckCircle } from 'lucide-react';

const gold = '#C8A96E';
const charcoal = '#1A1916';
const secondary = '#6B6862';
const tertiary = '#9A9690';
const divider = '#E8E6E2';

const SectionRule = () => (
  <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '0 auto 72px' }} />
);

const GoldButton = ({ href, onClick, children }) => {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: '10px',
    padding: '13px 28px', fontSize: '0.75rem', fontWeight: 500,
    letterSpacing: '0.18em', textTransform: 'uppercase',
    border: `1px solid ${gold}`, color: gold,
    backgroundColor: 'transparent', cursor: 'pointer',
    transition: 'background-color 0.25s, color 0.25s', textDecoration: 'none',
  };
  if (href) return (
    <a
      href={href} target="_blank" rel="noopener noreferrer" style={base}
      onMouseEnter={e => Object.assign(e.currentTarget.style, { backgroundColor: gold, color: '#fff' })}
      onMouseLeave={e => Object.assign(e.currentTarget.style, { backgroundColor: 'transparent', color: gold })}
    >{children}</a>
  );
  return (
    <button
      style={base} onClick={onClick}
      onMouseEnter={e => Object.assign(e.currentTarget.style, { backgroundColor: gold, color: '#fff' })}
      onMouseLeave={e => Object.assign(e.currentTarget.style, { backgroundColor: 'transparent', color: gold })}
    >{children}</button>
  );
};

const Label = ({ children }) => (
  <p style={{ color: gold, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '16px' }}>{children}</p>
);

const HomePage = ({ language = 'en', setCurrentPage }) => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) setSubscribed(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <div style={{ paddingTop: 'env(safe-area-inset-top)', position: 'sticky', top: 0, zIndex: 50, backgroundColor: '#fff' }} />

      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="pt-28 pb-16 px-6">
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '64px', flexWrap: 'wrap' }}>
          {/* Hero image — left */}
          <div style={{ flex: '0 0 auto', width: 'clamp(260px, 34vw, 440px)' }}>
            <img
              src="/heroimage.webp"
              alt="Zeus Hernandez"
              style={{ width: '100%', height: 'auto', display: 'block', objectFit: 'cover', borderRadius: '24px' }}
            />
          </div>
          {/* Text — right */}
          <div style={{ flex: '1 1 320px', minWidth: 0 }}>
            <Label>{language === 'es' ? 'Fundador y CEO, Infinity Development' : 'Founder & CEO, Infinity Development'}</Label>
            <h1
              className="font-light"
              style={{ color: charcoal, fontSize: 'clamp(2.8rem, 6vw, 5rem)', lineHeight: '1.1', letterSpacing: '-0.02em', marginBottom: '24px' }}
            >
              Zeus Hernandez
            </h1>
            <p
              className="font-light text-lg"
              style={{ color: secondary, lineHeight: '1.6', marginBottom: '20px' }}
            >
              {language === 'es'
                ? 'Desarrollador inmobiliario, constructor de plataformas y pensador de sistemas.'
                : 'Real Estate Developer, Platform Builder, and Systems Thinker.'}
            </p>
            <p
              className="font-light text-sm"
              style={{ color: tertiary, lineHeight: '2', marginBottom: '16px' }}
            >
              Operating at the intersection of real assets, institutional governance, and technical innovation — building sustainable living-sector platforms across the United States and Latin America.
            </p>
            <div style={{ width: '32px', height: '1px', backgroundColor: gold, marginBottom: '32px' }} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <GoldButton onClick={() => setCurrentPage && setCurrentPage('thesis')}>
                {language === 'es' ? 'Leer la Tesis' : 'Read the Thesis'}
              </GoldButton>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PROOF LINE ───────────────────────────────────────── */}
      <section className="py-12 px-6">
        <div style={{ maxWidth: '512px', margin: '0 auto', textAlign: 'center' }}>
          <p
            className="font-light text-sm"
            style={{ color: tertiary, lineHeight: '2', fontStyle: 'italic' }}
          >
            Infinity Development builds sustainable, high-performance multifamily and living-sector assets — integrating disciplined capital structuring, carbon-conscious design, and data-driven execution through Infinity Cortex.
          </p>
        </div>
      </section>

      {/* ─── PLATFORM PRINCIPLES ──────────────────────────────── */}
      <section id="infinity" className="pt-12 pb-20 px-6">
        <div style={{ maxWidth: '672px', margin: '0 auto' }}>
          <Label>{language === 'es' ? 'Principios de Plataforma' : 'Platform Principles'}</Label>
          <h2
            className="font-light"
            style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '48px' }}
          >
            {language === 'es' ? 'Principios de Plataforma' : 'Platform Principles'}
          </h2>
          {[
            {
              title: 'Institutional Governance',
              desc: 'Operating with the structure, transparency, and reporting standards required by long-term institutional capital — ensuring consistency across every stage of development.',
            },
            {
              title: 'Technical Innovation',
              desc: 'Leveraging Artificial Intelligence, Machine Learning, & Data-Driven Frameworks to improve decision-making, reduce risk, and accelerate execution across the development lifecycle.',
            },
            {
              title: 'Long-Horizon Stewardship',
              desc: 'Designing assets and systems that endure — prioritizing durability, adaptability, and long-term value creation across markets and generations.',
            },
          ].map((item, i) => (
            <div
              key={i}
              style={{ borderBottom: `1px solid ${divider}`, padding: '24px 0' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{ width: '6px', height: '1px', backgroundColor: gold, marginTop: '10px', flexShrink: 0 }} />
                <div>
                  <p className="font-normal text-base" style={{ color: charcoal, marginBottom: '8px' }}>{item.title}</p>
                  <p className="font-light text-sm" style={{ color: secondary, lineHeight: '1.9' }}>{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <SectionRule />

      {/* ─── ABOUT / IDENTITY ─────────────────────────────────── */}
      <section className="py-20 px-6">
        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
          <Label>{language === 'es' ? 'Identidad' : 'Identity'}</Label>
          <h2
            className="font-light"
            style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}
          >
            {language === 'es' ? 'Acerca de Zeus Hernandez' : 'About Zeus Hernandez'}
          </h2>
          {[
            "I'm Zeus Hernandez — Founder of Infinity Development and builder of a long-horizon platform at the intersection of innovation, capital, and real assets.",
            "My work focuses on designing and scaling systems that improve how we finance, build, and operate the built environment and beyond — across the United States and Latin America.",
            "This platform serves as a digital studio for ideas, execution, and long-term thinking — documenting both the work being done today and the systems being built for tomorrow.",
          ].map((para, i) => (
            <p key={i} className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{para}</p>
          ))}
          <div style={{ marginTop: '32px' }}>
            <GoldButton href="https://infinitydevelopment.com" >
              {language === 'es' ? 'Más sobre Infinity Development' : 'Learn More About Infinity Development'}
            </GoldButton>
          </div>
        </div>
      </section>

      <SectionRule />

      {/* ─── PLATFORM & OUTPUT ────────────────────────────────── */}
      <section id="writings" className="py-20 px-6">
        <div style={{ maxWidth: '672px', margin: '0 auto' }}>
          <Label>{language === 'es' ? 'Plataforma y Producción' : 'Platform & Output'}</Label>
          <h2
            className="font-light"
            style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '16px' }}
          >
            {language === 'es' ? 'Plataforma y Producción' : 'Platform & Output'}
          </h2>
          <p className="font-light text-sm" style={{ color: tertiary, lineHeight: '2', marginBottom: '40px' }}>
            A collection of conversations, frameworks, and field insights from the front lines of modern real estate and development.
          </p>
          {[
            { title: 'The Infinity Podcast', desc: 'Conversations with developers, investors, and operators shaping the future of the built environment.', cta: 'Listen Now' },
            { title: 'Writings', desc: 'Frameworks and essays on real estate development, capital strategy, and long-horizon systems.', cta: 'Read More' },
            { title: 'Shorts & Videos', desc: 'Visual breakdowns of development concepts, market insights, and execution strategies.', cta: 'Watch Now' },
            { title: 'Newsletter', desc: 'Weekly perspectives on real estate, venture, and long-term value creation.', cta: 'Subscribe' },
            { title: 'The AI-Enabled Developer', desc: 'Coming Q2 2026 — exploring how AI and sustainability are redefining development.', cta: 'Join Early List' },
          ].map((item, i) => (
            <div
              key={i}
              style={{ borderBottom: `1px solid ${divider}`, padding: '20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}
            >
              <div style={{ flex: 1 }}>
                <p className="font-normal text-sm" style={{ color: charcoal, marginBottom: '6px' }}>{item.title}</p>
                <p className="font-light" style={{ color: tertiary, fontSize: '0.75rem', lineHeight: '1.9' }}>{item.desc}</p>
              </div>
              <a
                href="#"
                style={{ color: gold, fontSize: '0.75rem', letterSpacing: '0.08em', fontWeight: 400, whiteSpace: 'nowrap', marginTop: '2px', textDecoration: 'none', flexShrink: 0 }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '0.7')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
              >
                {item.cta}
              </a>
            </div>
          ))}
        </div>
      </section>

      <SectionRule />

      {/* ─── TRANSITION TO INFINITY ───────────────────────────── */}
      <section className="py-20 px-6 text-center">
        <div style={{ maxWidth: '512px', margin: '0 auto' }}>
          <Label>{language === 'es' ? 'Mirando Más Allá' : 'Looking Beyond'}</Label>
          <h2
            className="font-light"
            style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '24px' }}
          >
            {language === 'es' ? 'Mirando Más Allá de la Plataforma Actual' : 'Looking Beyond the Current Platform'}
          </h2>
          <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '36px' }}>
            Infinity Development serves as the execution engine today — but it is not the endpoint. It is part of a broader system designed to expand across real assets, capital, and innovation — building interconnected platforms with long-horizon alignment and institutional discipline.
          </p>
          <GoldButton href="https://infinitydevelopment.com">
            {language === 'es' ? 'Explorar Infinity' : 'Explore Infinity'}
          </GoldButton>
        </div>
      </section>

      <SectionRule />

      {/* ─── NEWSLETTER ───────────────────────────────────────── */}
      <section className="py-20 px-6 text-center">
        <div style={{ maxWidth: '512px', margin: '0 auto' }}>
          <Label>{language === 'es' ? 'Boletín' : 'Newsletter'}</Label>
          <h2
            className="font-light"
            style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '16px' }}
          >
            {language === 'es' ? 'Mantente Conectado' : 'Stay Connected'}
          </h2>
          <p className="font-light text-sm" style={{ color: secondary, lineHeight: '1.9', marginBottom: '12px' }}>
            {language === 'es'
              ? 'Una carta semanal sobre bienes raíces, capital y sistemas de largo horizonte.'
              : 'A weekly letter on real estate, capital, and long-horizon systems.'}
          </p>
          <p className="font-light text-sm" style={{ color: tertiary, lineHeight: '2', marginBottom: '36px' }}>
            Insights on development, capital markets, sustainability, and the future of the built environment — written for operators, investors, and long-horizon thinkers.
          </p>

          {!subscribed ? (
            <form onSubmit={handleSubscribe} style={{ textAlign: 'left' }}>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={language === 'es' ? 'Tu correo electrónico' : 'Enter your email'}
                required
                style={{
                  width: '100%', padding: '12px 0', background: 'transparent',
                  border: 'none', borderBottom: `1px solid ${divider}`,
                  color: charcoal, fontSize: '1rem', fontWeight: 300,
                  outline: 'none', caretColor: gold, transition: 'border-color 0.25s',
                  marginBottom: '24px', boxSizing: 'border-box',
                }}
                onFocus={e => (e.target.style.borderBottomColor = gold)}
                onBlur={e => (e.target.style.borderBottomColor = divider)}
              />
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <GoldButton onClick={handleSubscribe}>
                  {language === 'es' ? 'Suscribirse' : 'Subscribe'}
                </GoldButton>
              </div>
            </form>
          ) : (
            <div style={{ paddingTop: '32px', paddingBottom: '32px' }}>
              <div
                style={{ width: '40px', height: '40px', borderRadius: '50%', border: `1px solid ${gold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}
              >
                <CheckCircle style={{ width: '16px', height: '16px', color: gold }} />
              </div>
              <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2' }}>
                {language === 'es' ? 'Suscrito. Nos vemos en tu bandeja.' : 'Subscribed. See you in your inbox.'}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
